import { DebugValuesProvider } from '@dealstack/frontend/shared/components/DebugMenu/useDebugValues';
import { ModalLayer } from '@dealstack/frontend/shared/components/fundament/Modal/ModalLayer';
import {
	FullLoaderScreenLoaderProvider,
	FullscreenLoader,
} from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { AppTypeProvider } from '@dealstack/frontend/shared/mechanism/appTypeContext/appTypeContext';
import { ConfigCatWrapper } from '@dealstack/frontend/shared/mechanism/configCatFrontend/configCatFrontend';
import { CookieScriptComponent } from '@dealstack/frontend/shared/mechanism/cookie-script/cookieScript';
import { setupDebugging } from '@dealstack/frontend/shared/mechanism/debugging/setup';
import { I18NLayer } from '@dealstack/frontend/shared/mechanism/i18n/i18nLayer';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { GlobalStyling } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { AppMantineProvider } from '@dealstack/frontend/shared/mechanism/styleguide/AppMantineProvider';
import { InitializeToastSystem } from '@dealstack/frontend/shared/mechanism/toast/InitializeToastSystem';
import { InitializeTooltips } from '@dealstack/frontend/shared/mechanism/tooltip/InitializeTooltips';
import { useAppSelector } from '@dealstack/frontend/admin/hooks/redux';
import { Routing } from '@dealstack/frontend/admin/pages/Routing/Routing';
import { store } from '@dealstack/frontend/admin/redux/store';
import { PROJECT } from '@dealstack/shared/lib/common/environment';
import * as Sentry from '@sentry/react';
import React, { Suspense } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { ApiLayer } from '@dealstack/frontend/admin/components/global/ApiLayer/ApiLayer';
import { getWebEnvironment } from '@dealstack/frontend/shared/environment';
import { getGlobalConfig, useGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { ToastV2Host } from '@dealstack/frontend/shared/mechanism/toast/ToastV2Host';
import { usePeriodicRefresh } from '@dealstack/frontend/shared/hooks/usePeriodicRefresh/usePeriodicRefresh.hook';
import { SessionTimeoutGuard } from '@dealstack/frontend/shared/components/SessionTimeout/SessionTimeoutGuard';
import { GenericError as GenericErrorPage } from '@dealstack/frontend/shared/pages/GenericError/GenericError';
import { initializeSentry } from '@dealstack/frontend/shared/sentry';
// @d.slaby 06.09.2023
// This is set of debugging tools, should not be enabled on production
setupDebugging();

const environment = getWebEnvironment();
const [group] = environment;

initializeSentry({
	dsn: getGlobalConfig().sentryApp,
	apiUrl: getGlobalConfig().apiUrl,
	environment,
	release: import.meta.env.VITE_SENTRY_RELEASE,
});

logger.setConfig({
	enviroment: group,
});

export const InitializeApplication = () => {
	return (
		<React.StrictMode>
			<GlobalStyling />
			<Sentry.ErrorBoundary fallback={<GenericErrorPage />}>
				<FullLoaderScreenLoaderProvider>
					<ThemeProvider theme={defaultTheme}>
						<AppMantineProvider>
							<ModalLayer>
								<InitializeTooltips />
								<Suspense fallback={<FullscreenLoader name="suspense" />}>
									<Provider store={store}>
										<DebugValuesProvider>
											<ToastV2Host />
											<RunInitialConfigurations />
										</DebugValuesProvider>
									</Provider>
								</Suspense>
							</ModalLayer>
						</AppMantineProvider>
					</ThemeProvider>
				</FullLoaderScreenLoaderProvider>
			</Sentry.ErrorBoundary>
		</React.StrictMode>
	);
};

const RunInitialConfigurations = () => {
	const globalState = useAppSelector((state) => state.global);
	const globalConfig = useGlobalConfig();
	const userState = useAppSelector((state) => state.user);

	usePeriodicRefresh(userState);

	if (!globalConfig) {
		logger.info('[InitializeApplication] Missing GlobalConfig');

		return <GenericErrorPage />;
	}

	if (!globalConfig.configCatSdkKey) {
		throw new Error('ConfigCat SDK missing in GlobalConfig');
	}

	return (
		<ConfigCatWrapper sdkKey={globalConfig.configCatSdkKey}>
			<CookieScriptComponent project={PROJECT.ADMIN} environment={getWebEnvironment()} />
			<AppTypeProvider appType={PROJECT.GODMODE}>
				<I18NLayer>
					{globalState?.isLoadingTranslations ? (
						<FullscreenLoader name="translations" />
					) : (
						<ApiLayer>
							{/* v6 navigated synchronously; v7 defaults to startTransition, which keeps the old route mounted while the next one renders. */}
							<BrowserRouter useTransitions={false}>
								{userState.isLoading ? <FullscreenLoader name="userState" /> : null}
								<Routing />
								<InitializeToastSystem />
								<SessionTimeoutGuard />
							</BrowserRouter>
						</ApiLayer>
					)}
				</I18NLayer>
			</AppTypeProvider>
		</ConfigCatWrapper>
	);
};
